/**
 * Admin API — AI 제공자 관리
 */

import apiClient from './client';

export interface ProviderStatus {
  connected: boolean;
  apiKeyMasked?: string;
  scope?: string;       // "tenant:xxx" | "customer:yyy" | "global"
  inherited?: boolean;  // 상위(테넌트)에서 상속된 설정이면 true
}

export type BedrockAuthType = 'assumeRole' | 'accessKey';

export interface BedrockAccount {
  id: string;
  scope: string;
  name: string;
  authType: BedrockAuthType;
  region: string;
  externalId?: string;
  roleArn?: string;
  accessKeyId?: string;
  secretAccessKey?: string;
  isActive?: boolean;
  inherited?: boolean;
  s3BucketName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBedrockAccountInput {
  name: string;
  authType: BedrockAuthType;
  region: string;
  roleArn?: string;
  externalId?: string;
  accessKeyId?: string;
  secretAccessKey?: string;
  scopeOverride?: string;
  s3BucketName?: string;
}

export interface ScopeOption {
  value: string;
  label: string;
  type: 'tenant' | 'customer' | 'team';
  parentValue?: string;
}

export const adminApi = {
  /** AI 제공자 연결 상태 조회 */
  async getProviderStatus(providerId: string, scope?: string): Promise<ProviderStatus> {
    const params = scope ? `?scope=${encodeURIComponent(scope)}` : '';
    const res = await apiClient.get<{ success: boolean; data: ProviderStatus }>(
      `/api/admin/ai-providers/${providerId}/status${params}`
    );
    return res.data.data;
  },

  /** API Key 저장 */
  async saveApiKey(providerId: string, apiKey: string, scopeOverride?: string): Promise<ProviderStatus> {
    const res = await apiClient.post<{ success: boolean; data: ProviderStatus }>(
      `/api/admin/ai-providers/${providerId}/apikey`,
      { apiKey, scopeOverride }
    );
    return res.data.data;
  },

  /** 연결 해제 */
  async deleteApiKey(providerId: string, scopeOverride?: string): Promise<void> {
    const params = scopeOverride ? `?scopeOverride=${encodeURIComponent(scopeOverride)}` : '';
    await apiClient.delete(`/api/admin/ai-providers/${providerId}/apikey${params}`);
  },

  /** 연결 테스트 */
  async testConnection(providerId: string, scopeOverride?: string): Promise<{ message: string }> {
    const res = await apiClient.post<{ success: boolean; data: { message: string } }>(
      `/api/admin/ai-providers/${providerId}/test`,
      scopeOverride ? { scopeOverride } : {}
    );
    return res.data.data;
  },

  // ── Bedrock 멀티 어카운트 ──────────────────────────────────────────────
  /** External ID 선발급 + 서비스 AWS 계정/Principal ARN */
  async getBedrockExternalId(): Promise<{ externalId: string; serviceAccountId: string; principalArn: string }> {
    const res = await apiClient.get<{ success: boolean; data: { externalId: string; serviceAccountId: string; principalArn: string } }>(
      '/api/admin/ai-providers/bedrock/external-id'
    );
    return res.data.data;
  },

  /** 어카운트 등록 대상 스코프 선택지 (팀/고객사/테넌트 트리) */
  async getBedrockScopeOptions(customerId?: string): Promise<ScopeOption[]> {
    const params = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
    const res = await apiClient.get<{ success: boolean; data: ScopeOption[] }>(
      `/api/admin/ai-providers/bedrock/scope-options${params}`
    );
    return res.data.data;
  },

  /** Bedrock 어카운트 목록 (특정 스코프 or 전체) */
  async listBedrockAccounts(scope?: string): Promise<BedrockAccount[]> {
    const params = scope ? `?scope=${encodeURIComponent(scope)}` : '';
    const res = await apiClient.get<{ success: boolean; data: BedrockAccount[] }>(
      `/api/admin/ai-providers/bedrock/accounts${params}`
    );
    return res.data.data;
  },

  /** Bedrock 어카운트 생성 */
  async createBedrockAccount(input: CreateBedrockAccountInput): Promise<BedrockAccount> {
    const res = await apiClient.post<{ success: boolean; data: BedrockAccount }>(
      '/api/admin/ai-providers/bedrock/accounts',
      input
    );
    return res.data.data;
  },

  /** Bedrock 어카운트 수정 */
  async updateBedrockAccount(accountId: string, input: Partial<CreateBedrockAccountInput>): Promise<BedrockAccount> {
    const res = await apiClient.patch<{ success: boolean; data: BedrockAccount }>(
      `/api/admin/ai-providers/bedrock/accounts/${accountId}`,
      input
    );
    return res.data.data;
  },

  /** Bedrock 어카운트 삭제 */
  async deleteBedrockAccount(accountId: string, scope?: string): Promise<void> {
    const params = scope ? `?scope=${encodeURIComponent(scope)}` : '';
    await apiClient.delete(`/api/admin/ai-providers/bedrock/accounts/${accountId}${params}`);
  },

  /** Bedrock 어카운트 활성화 (다른 계정은 비활성화) */
  async activateBedrockAccount(accountId: string): Promise<BedrockAccount> {
    const res = await apiClient.post<{ success: boolean; data: BedrockAccount }>(
      `/api/admin/ai-providers/bedrock/accounts/${accountId}/activate`
    );
    return res.data.data;
  },

  /** Bedrock 연결 테스트 (저장 전 - 입력값으로) */
  async testBedrockCredentials(input: Partial<CreateBedrockAccountInput>): Promise<{ message: string }> {
    const res = await apiClient.post<{ success: boolean; data: { message: string } }>(
      '/api/admin/ai-providers/bedrock/test',
      input
    );
    return res.data.data;
  },

  /** Bedrock 연결 테스트 (저장된 계정) */
  async testBedrockAccount(accountId: string): Promise<{ message: string }> {
    const res = await apiClient.post<{ success: boolean; data: { message: string } }>(
      `/api/admin/ai-providers/bedrock/accounts/${accountId}/test`
    );
    return res.data.data;
  },

  /** Bedrock 사용 가능한 모델 목록 조회 */
  async listBedrockModels(scope?: string): Promise<Array<{
    modelId: string;
    modelName: string;
    providerName: string;
  }>> {
    const params = scope ? `?scope=${encodeURIComponent(scope)}` : '';
    const res = await apiClient.get<{ success: boolean; data: Array<{
      modelId: string;
      modelName: string;
      providerName: string;
    }> }>(`/api/admin/ai-providers/bedrock/models${params}`);
    return res.data.data;
  },

  /** Google AI 사용 가능한 모델 목록 조회 */
  async listGoogleModels(): Promise<Array<{
    modelId: string;
    modelName: string;
    providerName: string;
  }>> {
    const res = await apiClient.get<{ success: boolean; data: Array<{
      modelId: string;
      modelName: string;
      providerName: string;
    }> }>('/api/admin/ai-providers/google/models');
    return res.data.data;
  },

  /** 모든 외부 제공자(bedrock 제외) 연결 상태 일괄 조회 */
  async getAllProviderStatuses(scope?: string): Promise<Record<string, ProviderStatus>> {
    const ids = ['anthropic', 'openai', 'google'];
    const results = await Promise.allSettled(ids.map((id) => adminApi.getProviderStatus(id, scope)));
    const map: Record<string, ProviderStatus> = {};
    ids.forEach((id, i) => {
      const r = results[i];
      map[id] = r?.status === 'fulfilled' ? r.value : { connected: false };
    });
    return map;
  },
};

export default adminApi;
